import { currencyToLocale } from '@libs/utils/helpers/currencyToLocale';
import { getPartnerImage } from '@libs/utils/helpers/getPartnerImage';

import { DebtResume, PartnerSystems, SortingDebtResponse } from './types';
import { queryStop } from '@common/services/queryStop';
import { Query } from '@tanstack/react-query';

// Constants
const RETRY_LIMIT = 3;
const POLLING_INTERVAL = 5000;

export const handlePolling = (query: Query<SortingDebtResponse>) => {
  const hasFailure = query.state.fetchFailureCount >= 3;
  const isCompleted = query.state.data?.status === 'COMPLETED';

  if (hasFailure) {
    return false;
  }

  if (isCompleted) {
    return false;
  }

  return POLLING_INTERVAL;
};

export const handleRetry = (failureCount: number, error: unknown) => {
  if (queryStop(error)) {
    return false;
  }

  const shouldRetry = failureCount < RETRY_LIMIT;
  return shouldRetry;
};

export const formatSortingDebts = (data: SortingDebtResponse) => {
  const { partnerSystems = [], status: debtStatus } = data as { partnerSystems: PartnerSystems[]; status: string };
  const isComplete = debtStatus === 'COMPLETED';

  if (!partnerSystems?.length) {
    return {
      ...data,
      formattedDebts: [],
    };
  }

  const onlyPartnersWithDebt = partnerSystems.filter((x) => x.show && x.debts.length > 0);

  const formattedDebts: DebtResume[] = [];
  for (const partner of onlyPartnersWithDebt) {
    for (const debt of partner.debts) {
      const payment = debt?.paymentOptions?.[0];

      const resume: DebtResume = {
        id: debt.id,
        partnerId: partner.id,
        partnerName: partner.name,
        productName: debt?.product,
        source: debt?.source,
        originalDebtDate: debt.details?.[0]?.originalDebtDate,
        partnerMessage: partner.promoMessage || '-',
        contract: debt?.contract,
        debtValue: currencyToLocale(payment.currentDebtValue),
        agreementValue: currencyToLocale(payment.agreementValue),
        discountValue: Math.floor((payment?.discountPercentage || 0) * 100),
        maxNumberInstallment: debt?.maxNumberInstallment,
        isLeadRedirect: partner?.isLeadRedirect,
        hasBvPartnership: partner?.hasBvsPartnership,
        debtsIsNegative: debt.debtIsNegative,
        isComplete,
        image: {
          src: getPartnerImage(partner.identifier),
          alt: `Logo ${partner.name}`,
        },
        redirectACData: {
          systemSlug: partner?.identifier,
          creditorSlug: partner?.partnerIdentifier,
          contract: debt?.contract,
          debtId: debt?.id,
          agreementValue: payment?.agreementValue,
        },
        redirectDebtsData: {
          identifier: partner?.identifier,
          partnerId: partner?.id,
          partnerName: partner?.partnerIdentifier,
        },
        engageTrackEvents: {
          partnerName: partner?.name,
          partnerSystemId: partner?.identifier,
          originalCompany: partner.partnerIdentifier,
          value: debt?.paymentOptions?.[0]?.agreementValue,
          updatedDebtValue: debt?.paymentOptions?.[0]?.currentDebtValue,
          agreementValue: debt?.paymentOptions?.[0]?.agreementValue,
          discountPercentage: debt?.paymentOptions?.[0]?.discountPercentage,
        },
      };
      formattedDebts.push(resume);
    }
  }

  return {
    formattedDebts,
  };
};
